import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ShieldCheck, Trash2 } from "lucide-react";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

/**
 * The privacy and security controls, for the settings page.
 *
 * These endpoints existed with nothing to reach them, which meant the GDPR
 * page told people they could export and delete their data from Settings and
 * Settings offered neither.
 *
 * Exports are built in the browser from the JSON the API returns rather than
 * asking the server for a file: the data is already being sent, and a second
 * download endpoint would be another place for a permission check to be
 * wrong.
 */
export default function PrivacyAndSecurity() {
  const { role, user } = useAuth();
  const canAdmin = can(role, "tenant.settings");

  const [twoFactor, setTwoFactor] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    api.get("/auth/2fa/status")
      .then(({ data }) => setTwoFactor(data))
      .catch(() => setTwoFactor(null));
  }, []);

  function download(filename, payload) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    // Revoked straight away; the browser has already taken what it needs and
    // holding the object keeps the whole export in memory.
    URL.revokeObjectURL(url);
  }

  async function exportSelf() {
    setBusy("me");
    try {
      const { data } = await api.get("/privacy/me/export");
      download(`socialmind-${user?.email || "me"}.json`, data);
      toast.success("Your data has been downloaded");
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function exportWorkspace() {
    setBusy("tenant");
    try {
      const { data } = await api.get("/privacy/export");
      download("socialmind-workspace.json", data);
      toast.success("Workspace data has been downloaded");
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function deleteMe() {
    setBusy("delete");
    try {
      const { data } = await api.post("/privacy/me/delete", { confirm_email: confirmEmail });
      toast.success(data.message);
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function resendVerification() {
    try {
      const { data } = await api.post("/auth/email/verify-request");
      toast.success(data.already_verified ? "Already verified" : "Verification email sent");
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  return (
    <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="privacy-security">
      <div className="mb-4 text-sm font-semibold text-[#0B2B33]">Privacy and security</div>

      <div className="space-y-4">
        {user && !user.email_verified ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#FFF4E2] px-4 py-3">
            <div className="text-sm text-[#43535E]">
              <span className="font-semibold text-[#0B2B33]">Confirm your email.</span>{" "}
              We only send approval and publishing updates to confirmed addresses.
            </div>
            <button onClick={resendVerification} data-testid="resend-verification"
              className="rounded-full border border-[#E4E9EF] bg-white px-3 py-1.5 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
              Send again
            </button>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E4E9EF] pb-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2B33]">
              <ShieldCheck className="h-4 w-4 text-[#16A46B]" /> Two-factor authentication
            </div>
            <p className="mt-0.5 text-sm text-[#43535E]">
              {twoFactor?.enabled
                ? `On. ${twoFactor.recovery_codes_remaining} recovery code${twoFactor.recovery_codes_remaining === 1 ? "" : "s"} left.`
                : twoFactor?.required
                  ? "Required for your account. Set it up from the platform console."
                  : "Off. Adds a code from your authenticator when you sign in."}
            </p>
          </div>
          <span className={`hs-chip ${twoFactor?.enabled ? "bg-[#E8FBF1] text-[#16A46B]" : "bg-[#F6F8FA] text-[#8A97A0]"}`}>
            {twoFactor?.enabled ? "Enabled" : "Not enabled"}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E4E9EF] pb-4">
          <div>
            <div className="text-sm font-semibold text-[#0B2B33]">Download your data</div>
            <p className="mt-0.5 text-sm text-[#43535E]">
              Everything held about you, as JSON.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={exportSelf} disabled={busy === "me"} data-testid="export-me"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA] disabled:opacity-60">
              <Download className="h-4 w-4" /> {busy === "me" ? "Preparing…" : "My data"}
            </button>
            {canAdmin ? (
              <button onClick={exportWorkspace} disabled={busy === "tenant"} data-testid="export-workspace"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA] disabled:opacity-60">
                <Download className="h-4 w-4" /> {busy === "tenant" ? "Preparing…" : "Whole workspace"}
              </button>
            ) : null}
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-[#B02328]">Delete your account</div>
              <p className="mt-0.5 max-w-xl text-sm text-[#43535E]">
                Your details are removed. Posts and approvals you worked on stay, attributed to a
                deleted user, so your colleagues' records keep their history.
              </p>
            </div>
            {!showDelete ? (
              <button onClick={() => setShowDelete(true)} data-testid="delete-account-open"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E3252A]/30 px-3 py-1.5 text-sm font-semibold text-[#B02328] hover:bg-[#FDECEC]">
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            ) : null}
          </div>

          {showDelete ? (
            <div className="mt-3 rounded-lg bg-[#FDECEC] p-4">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#B02328]">
                Type {user?.email} to confirm
              </label>
              <div className="flex flex-wrap gap-2">
                <input
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  data-testid="delete-confirm-email"
                  className="min-w-[240px] flex-1 rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm"
                  placeholder={user?.email}
                />
                <button onClick={deleteMe}
                  disabled={busy === "delete" || confirmEmail !== user?.email}
                  data-testid="delete-account-confirm"
                  className="rounded-full bg-[#E3252A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {busy === "delete" ? "Deleting…" : "Delete my account"}
                </button>
                <button onClick={() => { setShowDelete(false); setConfirmEmail(""); }}
                  className="rounded-full border border-[#E4E9EF] bg-white px-4 py-2 text-sm font-semibold text-[#0B2B33]">
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
